/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["dataset"];
import { fromJS, List, Map as ImmutableMap } from 'immutable';
import I18n from 'I18n';
import { generateDrilldownReport } from 'reporting-data/config/drilldown/generate';
import { TABLE } from 'reporting-data/constants/chartTypes';
import { SEARCH } from 'reporting-data/constants/configTypes';
import { EQ, BETWEEN } from 'reporting-data/constants/operators';
import { getFilter, getUpdatedDateRange } from 'reporting-data/config/drilldown/filters';
import { getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import makeDateRangeByType from 'reporting-data/lib/makeDateRangeByType';
import { isReportAccumulative, isTable, isTimeSeries, getDateProperty, getBreakdownField, getReportStartDate, getBucketField, getCustomWidgetType, DRILLED_WATERFALL_REPORT } from './helpers';
const REPORT_LIMIT = 10;
const getSnapshotDrilldownClosedateValues = report => {
  const filters = report.getIn(['config', 'filters', 'custom'], List());
  const closedateFilter = filters.find(f => f.get('property') === 'closedate' && f.get('operator') === BETWEEN);
  const closeDateRange = closedateFilter ? ImmutableMap({
    startDate: closedateFilter.get('value'),
    endDate: closedateFilter.get('highValue')
  }) : ImmutableMap(makeDateRangeByType({
    rangeType: 'ALL'
  }));
  return closeDateRange;
};
const getSnapshotsDrilldownfilters = report => {
  const filters = report.getIn(['config', 'filters', 'custom'], List());
  return filters.filter(filterMap => filterMap.get('property') !== 'dealstage.probability' && filterMap.get('property') !== 'closedate');
};
const getName = (report, row, dataset) => report.getIn(['config', 'dimensions']).map(dimension => getReferenceLabel(dataset, dimension, row.get(dimension))).join(' / ');
const getCommonDrilldownFilters = ({
  report,
  row
}) => {
  const dateProperty = getDateProperty(report);
  return report.getIn(['config', 'dimensions']).filter(dimension => dimension !== dateProperty).map(dimension => getFilter(dimension, ImmutableMap(), row.get(dimension), report.get('config')));
};
const getCommonDrilldownDateRange = ({
  report,
  row
}) => {
  const dateProperty = getDateProperty(report);
  return isTimeSeries(report) ? getUpdatedDateRange(row.get(dateProperty), report.get('config'), {}).update('startDate', startDate => isReportAccumulative(report) ? getReportStartDate(report) : startDate).update('endDate', date => I18n.moment(date).format('YYYY-MM-DD')) : null;
};
const getCommonDrilldownInfo = (report, {
  column,
  point,
  row,
  dataset
}) => {
  const breakdownField = getBreakdownField(report);
  const fullRow = isTable(report) ? breakdownField ? row.set(breakdownField, column.get('field')) : row : point.get('row');
  if (breakdownField && !fullRow.has(breakdownField)) {
    return null;
  }
  return {
    report,
    row: fullRow,
    name: getName(report, fullRow, dataset)
  };
};
const DRILLDOWN_INFOS = {
  DEAL_PUSH_ANALYSIS: (report, _ref) => {
    let {
        dataset
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return getCommonDrilldownInfo(report, Object.assign({
      dataset: isTable(report) ? dataset.setIn(['properties', 'pushStatus', 'references'], dataset.get('properties')) : dataset
    }, rest));
  }
};
const basicOnDrilldown = ({
  report,
  keys,
  name
}) => {
  return generateDrilldownReport(report, keys).then(drilledReport => {
    return drilledReport.setIn(['config', 'limit'], REPORT_LIMIT).set('name', name);
  });
};
const commonOnDrilldown = ({
  report,
  row,
  name
}) => {
  const breakdownField = getBreakdownField(report);
  const drilldownFilters = getCommonDrilldownFilters({
    report,
    row
  });
  const dateRangeFilter = getCommonDrilldownDateRange({
    report,
    row
  });
  const drilldownReport = report.set('name', name).set('chartType', TABLE).setIn(['config', 'configType'], SEARCH).setIn(['config', 'offset'], 0).setIn(['config', 'dimensions'], breakdownField ? List.of(breakdownField) : List()).updateIn(['config', 'filters', 'dateRange', 'value'], value => dateRangeFilter || value).updateIn(['config', 'filters', 'custom'], List(), filters => filters.concat(drilldownFilters));
  return Promise.resolve(drilldownReport);
};
const onWaterfallDrilldown = ({
  report,
  row,
  name
}) => Promise.resolve(report.set('chartType', TABLE).setIn(['config', 'configType'], SEARCH).set('name', name).setIn(['config', 'metrics'], fromJS([{
  property: 'dealname'
}, {
  property: 'closedate'
}, {
  property: 'dealstage'
}, {
  property: 'pipeline'
}, {
  property: 'amount_in_home_currency'
}, {
  property: 'createdate'
}])).updateIn(['config', 'filters', 'custom'], List(), filters => filters.push(ImmutableMap({
  property: 'dealBreakdown',
  operator: EQ,
  value: row.get('dealBreakdown')
}))));
const onPushAnalysisDrilldown = ({
  report,
  row,
  name
}) => {
  const bucketField = getBucketField(report);
  const breakdownField = getBreakdownField(report);
  return Promise.resolve(report.set('chartType', TABLE).setIn(['config', 'configType'], SEARCH).set('name', name).updateIn(['config', 'filters', 'custom'], filters => filters.concat(fromJS([{
    property: breakdownField,
    operator: EQ,
    value: row.get(breakdownField)
  }, {
    operator: EQ,
    property: 'groupKey',
    value: row.get(bucketField)
  }, {
    operator: EQ,
    property: 'groupingDimension',
    value: bucketField
  }]))));
};
const onChangeHistoryDrilldown = ({
  report,
  row,
  name
}) => {
  const changeType = row.get('deal_change_type');
  return Promise.resolve(report.set('name', name).set('chartType', TABLE).setIn(['config', 'configType'], SEARCH).updateIn(['config', 'filters', 'custom'], List(), filters => filters.push(ImmutableMap({
    property: 'deal_change_type',
    operator: EQ,
    value: changeType
  }))));
};
const onPipelineSnpapshotsDrilldown = ({
  report,
  row,
  name
}) => {
  if (row.get('dealstage') !== 'deal_activity') {
    return commonOnDrilldown({
      report,
      row,
      name
    });
  }
  const drilldownFilters = getSnapshotsDrilldownfilters(report);
  const closeDateRange = getSnapshotDrilldownClosedateValues(report);
  const dateRangeValue = report.getIn(['config', 'filters', 'dateRange', 'value']);
  const dateRangeFilter = getCommonDrilldownDateRange({
    report,
    row
  });
  const startDate = closeDateRange.get('startDate') || dateRangeValue.get('startDate');
  const endDate = closeDateRange.get('endDate') || dateRangeValue.get('endDate');
  return Promise.resolve(DRILLED_WATERFALL_REPORT.set('name', name).setIn(['config', 'filters', 'dateRange', 'value'], dateRangeFilter || dateRangeValue).updateIn(['config', 'filters', 'custom'], List(), filters => filters.concat(drilldownFilters).concat(fromJS([{
    operator: 'GT',
    property: 'closedate',
    value: startDate,
    dateTimeFormat: 'DATE'
  }, {
    operator: 'LT',
    property: 'closedate',
    value: endDate,
    dateTimeFormat: 'DATE'
  }]))));
};
const generateDrilldowns = {
  DEAL_PUSH_ANALYSIS: onPushAnalysisDrilldown,
  PIPELINE_SNAPSHOTS: onPipelineSnpapshotsDrilldown,
  WATERFALL: onWaterfallDrilldown,
  SALES_QUOTA: commonOnDrilldown,
  CHANGE_HISTORY_DEAL_LIST: onChangeHistoryDrilldown
};
export const callWidgetDrilldown = (report, onDrilldown) => drilldownProps => {
  const widgetType = getCustomWidgetType(report);
  const getDrilldownInfo = DRILLDOWN_INFOS[widgetType] || getCommonDrilldownInfo;
  const drilldownInfo = getDrilldownInfo(report, drilldownProps);
  if (!drilldownInfo) {
    return;
  }
  const generateDrilldown = generateDrilldowns[widgetType] || basicOnDrilldown;
  generateDrilldown(drilldownInfo).then(onDrilldown);
};